import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer
} from "recharts";
import { getTaskStats } from "../../services/taskDashboard";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    height: "100%",
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
  },
  statNumber: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  chartContainer: {
    height: 300,
    marginTop: theme.spacing(2),
  },
  filters: {
    marginBottom: theme.spacing(3),
    "& > *": {
      margin: theme.spacing(1),
      minWidth: 200,
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  dashboardTitle: {
    color: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') ? '#FFFFFF' : theme.palette.text.primary,
    marginBottom: 24,
  },
}));

const COLORS = {
  pending: "#ff9800",
  completed: "#4caf50",
  low: "#2196f3",
  medium: "#ff9800",
  high: "#f44336",
  overdue: "#f44336",
  dueToday: "#ff9800",
  dueThisWeek: "#2196f3",
  dueThisMonth: "#9c27b0",
  noDate: "#9e9e9e",
};

const TaskDashboard = () => {
  const classes = useStyles();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    period: "thisMonth",
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    fetchStats();
  }, [filters]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (filters.startDate && filters.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }

      const { data } = await getTaskStats(params);
      setStats(data);
    } catch (err) {
      console.error("Erro ao buscar estatísticas:", err);
      setError("Erro ao carregar estatísticas do dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatStatusData = () => {
    if (!stats) return [];
    return [
      { name: "Pendentes", value: stats.statusStats.pending, color: COLORS.pending },
      { name: "Concluídas", value: stats.statusStats.completed, color: COLORS.completed },
    ];
  };

  const formatPriorityData = () => {
    if (!stats) return [];
    return [
      { name: "Baixa", value: stats.priorityStats.low, color: COLORS.low },
      { name: "Média", value: stats.priorityStats.medium, color: COLORS.medium },
      { name: "Alta", value: stats.priorityStats.high, color: COLORS.high },
    ];
  };

  const formatDueDateData = () => {
    if (!stats) return [];
    return [
      { name: "Atrasadas", value: stats.dueDateStats.overdue, color: COLORS.overdue },
      { name: "Hoje", value: stats.dueDateStats.dueToday, color: COLORS.dueToday },
      { name: "Esta Semana", value: stats.dueDateStats.dueThisWeek, color: COLORS.dueThisWeek },
      { name: "Este Mês", value: stats.dueDateStats.dueThisMonth, color: COLORS.dueThisMonth },
      { name: "Sem Data", value: stats.dueDateStats.noDate, color: COLORS.noDate },
    ];
  };

  const formatProductivityData = () => {
    if (!stats) return [];
    return [
      {
        period: "Esta Semana",
        criadas: stats.productivityStats.createdThisWeek,
        concluidas: stats.productivityStats.completedThisWeek,
      },
      {
        period: "Este Mês",
        criadas: stats.productivityStats.createdThisMonth,
        concluidas: stats.productivityStats.completedThisMonth,
      },
    ];
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.container}>
        <Paper style={{ padding: 16, backgroundColor: '#ffebee' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h4" gutterBottom className={classes.dashboardTitle}>
        Dashboard de Tarefas
      </Typography>

      {/* Filtros */}
      <Box className={classes.filters}>
        <FormControl variant="outlined">
          <InputLabel>Período</InputLabel>
          <Select
            value={filters.period}
            onChange={(e) => setFilters({ ...filters, period: e.target.value })}
            label="Período"
          >
            <MenuItem value="thisWeek">Esta Semana</MenuItem>
            <MenuItem value="thisMonth">Este Mês</MenuItem>
            <MenuItem value="custom">Personalizado</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Cards de Resumo */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statNumber}>
                {stats?.statusStats.total || 0}
              </Typography>
              <Typography className={classes.statLabel}>
                Total de Tarefas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statNumber} style={{ color: COLORS.completed }}>
                {stats?.statusStats.completed || 0}
              </Typography>
              <Typography className={classes.statLabel}>
                Concluídas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statNumber} style={{ color: COLORS.pending }}>
                {stats?.statusStats.pending || 0}
              </Typography>
              <Typography className={classes.statLabel}>
                Pendentes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statNumber} style={{ color: COLORS.overdue }}>
                {stats?.dueDateStats.overdue || 0}
              </Typography>
              <Typography className={classes.statLabel}>
                Atrasadas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={3}>
        {/* Status das Tarefas */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Status das Tarefas
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={formatStatusData()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {formatStatusData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Prioridades */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Distribuição por Prioridade
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={formatPriorityData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8">
                  {formatPriorityData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Vencimentos */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Tarefas por Vencimento
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={formatDueDateData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8">
                  {formatDueDateData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Produtividade */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Produtividade
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={formatProductivityData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="criadas" fill="#2196f3" name="Criadas" />
                <Bar dataKey="concluidas" fill="#4caf50" name="Concluídas" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Tendência Semanal */}
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Tendência Semanal
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats?.weeklyTrend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="created" stroke="#2196f3" name="Criadas" />
                <Line type="monotone" dataKey="completed" stroke="#4caf50" name="Concluídas" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Top Categorias */}
        {stats?.categoryStats && stats.categoryStats.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper className={classes.paper}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Top Categorias
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.categoryStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#9c27b0" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default TaskDashboard;
